import { useEffect, useState } from 'react'
import { NavigateFunction, useLocation } from 'react-router-dom'

import Container from '@/components/atoms/container/container'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'
import { TransactionService } from '@/services/transactionService'
import { VoucherResponse } from '@/types/pos/deposit'

type BettingVendorAccountTemplateProps = {
    navigate: NavigateFunction
}

export default function BettingVendorAccountTemplate({
    navigate
}: BettingVendorAccountTemplateProps) {
    const { t } = useTranslate()
    const location = useLocation()
    const { state, setCurrentStep, setDirectBettingAccountId, setCashDetected } =
        useOrder()
    const locationState = location.state as
        | {
              fromDirectBettingConfirmation?: boolean
              autoRetryDeposit?: boolean
              selectedBettingService?: string
              accountId?: string
              createdVoucherData?: VoucherResponse
          }
        | undefined
    const persistedVoucher =
        state.createdVoucherData ?? locationState?.createdVoucherData ?? null
    // We are returning to the direct-betting confirmation screen whenever we
    // already have a created voucher in context (or, defensively, in the
    // navigation state). Relying on context as the source of truth keeps the
    // voucher recoverable regardless of how the user navigated here (in-page
    // buttons, header back, etc.).
    const isReturnToDirectConfirmation =
        Boolean(persistedVoucher) ||
        locationState?.fromDirectBettingConfirmation === true
    const [accountId, setAccountId] = useState(
        locationState?.accountId ?? state.directBettingAccountId ?? ''
    )
    const [draftAmount, setDraftAmount] = useState(0)
    const selectedVendor = state.selectedBettingVendor
    const shouldLockToCash =
        state.paymentMethod === 'cash' &&
        (state.cashDetected || draftAmount > 0)

    useEffect(() => {
        if (!state.directBettingCompleted) {
            return
        }

        setCurrentStep(VoucherPurchaseStep.PROCESS_PAYMENT)
        navigate('/buy-voucher-cash', {
            replace: true,
            state: {
                voucherType: 'betting',
                bettingFlow: 'direct',
                selectedBettingService:
                    locationState?.selectedBettingService ??
                    selectedVendor?.slug,
                accountId:
                    locationState?.accountId ??
                    state.directBettingAccountId ??
                    accountId,
                createdVoucherData: persistedVoucher
            }
        })
    }, [
        accountId,
        locationState?.accountId,
        locationState?.selectedBettingService,
        navigate,
        persistedVoucher,
        selectedVendor?.slug,
        setCurrentStep,
        state.directBettingAccountId,
        state.directBettingCompleted
    ])

    useEffect(() => {
        if (state.directBettingCompleted) {
            return
        }
        if (!selectedVendor) {
            navigate('/betting-service-selection')
        }
    }, [navigate, selectedVendor, state.directBettingCompleted])

    useEffect(() => {
        let isMounted = true

        TransactionService.GetVoucherAmount()
            .then((response) => {
                if (!isMounted) return
                const amount =
                    typeof response?.amount === 'number' ? response.amount : 0
                setDraftAmount(amount)
                if (amount > 0) setCashDetected(true)
            })
            .catch(() => {
                if (!isMounted) return
                setDraftAmount(0)
            })

        return () => {
            isMounted = false
        }
    }, [setCashDetected])

    const appendDigit = (digit: number) => {
        setAccountId((prev) => `${prev}${digit}`.slice(0, 12))
    }

    const clearInput = () => setAccountId('')
    const deleteLastDigit = () => setAccountId((prev) => prev.slice(0, -1))
    const hasCompletedVoucher = Boolean(persistedVoucher)

    const onHelpLinkClick = () => {
        if (hasCompletedVoucher) {
            setCurrentStep(VoucherPurchaseStep.VOUCHER_CONFIRMATION)
            navigate('/buy-voucher-cash', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: 'direct',
                    selectedBettingService:
                        locationState?.selectedBettingService ??
                        selectedVendor?.slug,
                    accountId,
                    fromDirectBettingConfirmation: true,
                    createdVoucherData: persistedVoucher
                }
            })
            return
        }

        if (shouldLockToCash) {
            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
            navigate('/buy-voucher-cash', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: 'direct',
                    selectedBettingService: selectedVendor?.slug,
                    accountId
                }
            })
            return
        }

        setCurrentStep(VoucherPurchaseStep.SELECT_PAYMENT_METHOD)
        navigate('/payment-method', {
            state: {
                voucherType: 'betting',
                bettingFlow: 'direct',
                selectedBettingService: selectedVendor?.slug,
                accountId
            }
        })
    }

    const goToInsertCash = () => {
        if (state.directBettingCompleted) return
        if (!selectedVendor || !accountId) return

        setDirectBettingAccountId(accountId)

        if (isReturnToDirectConfirmation) {
            setCurrentStep(VoucherPurchaseStep.DEPOSIT_SUCCESS_ANIMATION)
            navigate('/buy-voucher-cash', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: 'direct',
                    selectedBettingService: selectedVendor.slug,
                    accountId,
                    fromDirectBettingConfirmation: true,
                    autoRetryDeposit: true,
                    createdVoucherData: persistedVoucher
                }
            })
            return
        }

        if (shouldLockToCash) {
            setCurrentStep(VoucherPurchaseStep.INSERT_CASH)
            navigate('/buy-voucher-cash', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: 'direct',
                    selectedBettingService: selectedVendor.slug,
                    accountId
                }
            })
            return
        }

        setCurrentStep(VoucherPurchaseStep.SELECT_PAYMENT_METHOD)
        navigate('/payment-method', {
            state: {
                voucherType: 'betting',
                bettingFlow: 'direct',
                selectedBettingService: selectedVendor.slug,
                accountId
            }
        })
    }

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl="/betting-service-selection"
                navigationBackText={t('header.back')}
            />
            <div className="betting-vendor-account">
                {state.amount !== null && (
                    <div className="betting-vendor-account__amount">
                        {t('bettingServiceSelection.amountLabel', {
                            amount: state.amount
                        })}
                    </div>
                )}

                <h1>{selectedVendor?.name.toUpperCase() || ''}</h1>

                {selectedVendor?.squareImageUrl && (
                    <div className="betting-vendor-account__vendor-circle">
                        <img src={selectedVendor.squareImageUrl} alt="" />
                    </div>
                )}

                <button
                    className="betting-vendor-account__change-btn"
                    onClick={() => {
                        if (isReturnToDirectConfirmation) {
                            setDirectBettingAccountId(accountId)
                        }
                        navigate('/betting-service-selection', {
                            state: isReturnToDirectConfirmation
                                ? {
                                      fromDirectBettingConfirmation: true,
                                      selectedBettingService:
                                          selectedVendor?.slug,
                                      accountId,
                                      createdVoucherData: persistedVoucher
                                  }
                                : undefined
                        })
                    }}>
                    {t('bettingVendorAccount.changeSelection')}
                </button>

                <p className="betting-vendor-account__label">
                    {t('bettingVendorAccount.inputLabel')}
                </p>

                <div className="betting-vendor-account__input-wrap">
                    <input
                        value={accountId}
                        readOnly
                        className="betting-vendor-account__input"
                    />
                    <button
                        className="betting-vendor-account__input-clear"
                        onClick={clearInput}
                        aria-label={t('bettingVendorAccount.clear')}>
                        ×
                    </button>
                </div>

                <button
                    className="betting-vendor-account__hint"
                    onClick={onHelpLinkClick}>
                    {t('bettingVendorAccount.helpLink')}
                </button>

                <div className="betting-vendor-account__keypad">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                        <button
                            key={digit}
                            onClick={() => appendDigit(digit)}
                            className="betting-vendor-account__key">
                            {digit}
                        </button>
                    ))}
                    <div />
                    <button
                        onClick={() => appendDigit(0)}
                        className="betting-vendor-account__key">
                        0
                    </button>
                    <button
                        onClick={deleteLastDigit}
                        className="betting-vendor-account__key betting-vendor-account__key--delete"
                        aria-label={t('bettingVendorAccount.delete')}>
                        ⌫
                    </button>
                </div>

                <button
                    className="betting-vendor-account__continue"
                    disabled={!accountId}
                    onClick={goToInsertCash}>
                    {t('bettingVendorAccount.continue')}
                </button>
            </div>
            <Footer />
        </Container>
    )
}
